import type { ForgotPasswordParams } from "@nlc/shared-types";

import { zodResolver } from "@hookform/resolvers/zod";
import { forgotPasswordParamsZod } from "@nlc/shared-types";
import { useIsMutating } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import LoginLogoMark from "@/assets/login/logo-mark.svg?react";
import { Button } from "@/components/button";
import { FieldError, Input, Label, TextField } from "@/components/field";
import { toast } from "@/components/toast/toast-queue";
import { AuthContainer } from "@/features/auth/components/AuthContainer";
import { useWhitelabel } from "@/hooks/useWhitelabel";

import { useForgotPasswordMutation } from "./hooks/useForgotPasswordMutation";

export function ForgotPasswordPage() {
  const [successMessage, setSuccessMessage] = useState(false);
  const { mutate } = useForgotPasswordMutation();
  const navigate = useNavigate();
  const { isWhitelabel, loading } = useWhitelabel();

  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset,
  } = useForm<ForgotPasswordParams>({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(forgotPasswordParamsZod),
  });

  const onSubmit = (data: ForgotPasswordParams) => {
    mutate(data, {
      onError: () => {
        toast.add(
          { title: "Email not found", type: "error" },
          { timeout: 5000 },
        );
      },
      onSuccess: () => {
        setSuccessMessage(true);
        reset();
      },
    });
  };

  const isLoading = useIsMutating({ mutationKey: ["forgot-password"] });

  if (loading) {
    return null;
  }

  if (isWhitelabel) {
    return (
      <AuthContainer
        description="Forgot your password? No problem. Just let us know your email address and we will email you a password reset link that will allow you to choose a new one."
        title="Password recovery"
      >
        {successMessage ? (
          <div className="bg-bg-success-secondary p-10 text-center rounded-lg border-s-utility-green-200">
            <p>
              We have emailed your password reset link! Check your email inbox.
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-center flex-col relative">
            <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-col">
                <Controller
                  control={control}
                  name="email"
                  render={({
                    field: { name, onBlur, onChange, ref, value },
                    fieldState: { error, invalid },
                  }) => (
                    <TextField
                      className="w-90"
                      isInvalid={invalid}
                      isRequired
                      name={name}
                      onBlur={onBlur}
                      onChange={onChange}
                      validationBehavior="aria"
                      value={value}
                    >
                      <Label>Email</Label>
                      <div className="relative mb-6">
                        <Input
                          placeholder="Enter your email"
                          ref={ref}
                          type="text"
                        />
                      </div>
                      <FieldError>{error?.message}</FieldError>
                    </TextField>
                  )}
                  rules={{ required: "Email is required." }}
                />
                <Button isDisabled={!!isLoading || !isValid} type="submit">
                  {isLoading ? "Sending email..." : "Recovery password"}
                </Button>
                <Button
                  className="mt-2"
                  onPress={() => navigate({ to: "/login" })}
                  variant="secondary"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}
      </AuthContainer>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center bg-white px-2xl py-4xl">
      <div className="flex w-full max-w-[360px] flex-col gap-4xl">
        {successMessage ? (
          <div className="flex flex-col items-center gap-2xl text-center">
            <LoginLogoMark className="size-[76px]" />
            <h1 className="text-display-sm font-semibold text-login-heading">
              Email sent!
            </h1>
            <p className="text-md text-login-body">
              The password reset email has been sent to your registered address.
              Make sure to also check your spam folder.
            </p>
            <Button
              className="w-full bg-login-button-bg hover:bg-login-button-bg/90"
              onPress={() => navigate({ to: "/login" })}
            >
              Return to login
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center gap-2xl text-center">
              <LoginLogoMark className="size-[76px]" />
              <h1 className="text-display-sm font-semibold text-login-heading">
                Password recovery
              </h1>
              <p className="text-md text-login-body">
                Forgot your password? No problem. Just let us know your email
                address and we will email you a password reset link that will
                allow you to choose a new one.
              </p>
            </div>

            <form
              className="flex flex-col gap-3xl"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Controller
                control={control}
                name="email"
                render={({
                  field: { name, onBlur, onChange, ref, value },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    isInvalid={invalid}
                    isRequired
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    validationBehavior="aria"
                    value={value}
                  >
                    <Label>Email</Label>
                    <Input
                      className="border-login-border"
                      placeholder="Enter your email"
                      ref={ref}
                      type="text"
                    />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
                rules={{ required: "Email is required." }}
              />
              <div className="flex flex-col gap-lg">
                <Button
                  className="bg-login-button-bg hover:bg-login-button-bg/90"
                  isDisabled={!!isLoading || !isValid}
                  type="submit"
                >
                  {isLoading ? "Sending email..." : "Send password reset email"}
                </Button>
                <Button
                  color="gray"
                  onPress={() => navigate({ to: "/login" })}
                  variant="secondary"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
